import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Sparkle, Fire, BookOpen, FilmStrip, Heart, MagnifyingGlass, Microphone,
  MusicNotes, Users, HandsPraying, BookBookmark, Scroll, Hash, Church,
  ChatCircleDots, Clock, NotePencil, VideoCamera, BellSimpleRinging,
  Stethoscope, Queue, UserCircle, House, CaretLeft, CaretRight, Newspaper,
  ForkKnife, PuzzlePiece,
} from "@phosphor-icons/react";
import BrandMarkOutline from "./BrandMarkOutline";
import UserAvatar from "./UserAvatar";
import { useAuth } from "../context/AuthContext";

/**
 * CategoryChipRail — a sticky, horizontally-swipeable category rail
 * that lives right under the main Header. Models YouTube / Spotify
 * style chip strips so the user can swipe through every section of
 * the app without ever opening the "More" dropdown.
 *
 * Behavior:
 *   • Horizontal scroll with touch / trackpad. Hidden scrollbar but
 *     functional momentum-scroll on mobile.
 *   • On desktop we show ◀ / ▶ chevrons that scroll the rail by
 *     roughly one screen-width per click.
 *   • The currently-active route's chip is highlighted (terracotta
 *     fill, cream text) and auto-scrolled into view on mount.
 *   • A "House" all-channels home chip pins to the left so the user
 *     can always one-tap back to the homepage.
 */

// Single source of truth for every category we want exposed.
//
// Ordering (user request 2026-02-20): "Home" and "Trending" stay
// pinned at indexes 0 + 1 so the user always has a one-tap safety
// net + the daily community-pull. **Everything else is sorted
// alphabetically by label** so the rail is now predictable — if you
// know the section's name, you can jump to it without learning a
// custom order. We also include every Subject + Room and every
// utility (Search, Studio, Settings, etc.) so the rail truly
// replaces the More-menu for navigation.
//
// Featured chip (user request 2026-02-21): "Faith-Based Rap" sits in
// pinned slot #2 (index 2) so it surfaces immediately after Home +
// Trending — most Bible apps don't carry this genre, and the founder
// wants visitors to discover it without scrolling. It still appears
// at its alphabetical position would be (between Bookmarks + Choir)
// but we deduplicate so it only renders once.
const _PINNED = [
  { to: "/",              labelKey: "chip.home",         label: "Home",            icon: House,      pinned: true },
  { to: "/foryou",        labelKey: "chip.forYou",       label: "For You",         icon: Sparkle,    pinned: true,
    // Promote the new personalized feed for the first 30 days so
    // existing users notice it. Auto-disappears on/after the
    // expiry date — no follow-up commit needed.
    badge: "NEW", badgeUntil: "2026-04-01" },
  { to: "/discover",      labelKey: "chip.trending",     label: "Trending",        icon: Fire,       pinned: true },
  { to: "/christian-rap", labelKey: "chip.christianRap", label: "Faith-Based Rap", icon: Microphone, pinned: true,
    // "NEW" badge for the first 30 days after the chip got promoted
    // to the pinned slot (2026-02-21). Auto-disappears on/after the
    // expiry date — we don't have to ship code to remove it.
    badge: "NEW", badgeUntil: "2026-03-23" },
  // Pinned to the front of the rail (Feb 2026) so users can jump
  // straight into the Wednesday LiveKit video study session without
  // scrolling through the alphabetical chips. The page also still
  // lives under "S" in the alphabetical list (dedupe drops the
  // duplicate). NEW badge surfaces it for 30 days.
  { to: "/study-group",   labelKey: "chip.studyGroup",   label: "Study Group",     icon: Users,      pinned: true,
    badge: "NEW", badgeUntil: "2026-03-30" },
];
const _ALPHA = [
  { to: "/live",                  labelKey: "chip.live",                label: "Live",                 icon: BellSimpleRinging,
    // Replaces the old "Animated Bible" chip (user request 2026-02-26).
    // The Animated Bible page is still reachable via the main /videos
    // route — we just dropped it from the rail to surface Live streams
    // instead, which is where the activity is.
    badge: "LIVE" },
  { to: "/apocrypha",             labelKey: "chip.apocrypha",          label: "Apocrypha",            icon: Scroll },
  { to: "/articles",              label: "Articles",                   icon: NotePencil,
    badge: "NEW", badgeUntil: "2026-10-01" },
  { to: "/bible",                 labelKey: "chip.bible",              label: "Bible",                icon: BookOpen },
  { to: "/precepts",              labelKey: "chip.biblePrecepts",      label: "Bible Precepts",       icon: Scroll },
  { to: "/movies/black-american", labelKey: "chip.blackAmericanFilms", label: "Black American Films", icon: FilmStrip },
  { to: "/bookmarks",             labelKey: "chip.bookmarks",          label: "Bookmarks",            icon: Heart },
  { to: "/christian-rap",         labelKey: "chip.christianRap",       label: "Faith-Based Rap",      icon: Microphone },
  { to: "/choir",                 labelKey: "chip.choir",              label: "Choir",                icon: Users },
  { to: "/churches",              labelKey: "chip.churches",           label: "Churches",             icon: Church },
  { to: "/comedy",                labelKey: "chip.comedy",             label: "Comedy",               icon: VideoCamera },
  { to: "/community-shorts",      labelKey: "chip.communityShorts",    label: "Community Shorts",     icon: VideoCamera },
  { to: "/concordance",           labelKey: "chip.concordance",        label: "Concordance",          icon: BookOpen },
  { to: "/congregation",          labelKey: "chip.congregationLive",   label: "Congregation Live",    icon: Hash },
  { to: "/studio",                labelKey: "chip.creatorStudio",      label: "Creator Studio",       icon: NotePencil },
  { to: "/diagnostics",           labelKey: "chip.deviceCheck",        label: "Device Check",         icon: Stethoscope },
  { to: "/dictionary",            labelKey: "chip.dictionary",         label: "Dictionary",           icon: BookOpen },
  { to: "/shorts",                labelKey: "chip.discoverShorts",     label: "Discover Shorts",      icon: Sparkle },
  { to: "/news",                  labelKey: "chip.faithNews",          label: "Faith News",           icon: Newspaper },
  { to: "/comedy/foundational",   labelKey: "chip.foundationalComedy", label: "Foundational Comedy",  icon: VideoCamera },
  { to: "/friends",               labelKey: "chip.friends",            label: "Friends",              icon: Users },
  { to: "/gospel-music",          labelKey: "chip.gospelMusic",        label: "Gospel Music",         icon: MusicNotes },
  { to: "/kids",                  labelKey: "chip.kidsCartoons",       label: "Kids & Cartoons",      icon: BookBookmark },
  { to: "/memorial",              labelKey: "chip.memorial",           label: "Memorial",             icon: Heart },
  { to: "/messages",              labelKey: "chip.messages",           label: "Messages",             icon: ChatCircleDots },
  { to: "/movies",                labelKey: "chip.movies",             label: "Movies",               icon: FilmStrip },
  { to: "/music-videos",          labelKey: "chip.musicVideos",        label: "Music Videos",         icon: FilmStrip },
  { to: "/my-lineup",             labelKey: "chip.myLineup",           label: "My Lineup",            icon: Queue },
  { to: "/my-mix",                labelKey: "chip.myMix",              label: "My Mix",               icon: MusicNotes },
  { to: "/my-notes",              labelKey: "chip.myNotes",            label: "My Notes",             icon: NotePencil },
  { to: "/my-prayers",            labelKey: "chip.myPrayers",          label: "My Prayers",           icon: HandsPraying },
  { to: "/profile",               labelKey: "chip.myProfile",          label: "My Profile",           icon: UserCircle },
  { to: "/storybook",             labelKey: "chip.pictureBible",       label: "Picture Bible",        icon: Sparkle },
  { to: "/playlists",             label: "Playlists",                  icon: Queue },
  { to: "/circles",               labelKey: "chip.prayerCircles",      label: "Prayer Circles",       icon: HandsPraying },
  { to: "/prayer-wall",           labelKey: "chip.prayers",            label: "Prayers",              icon: HandsPraying },
  { to: "/crossword",             labelKey: "chip.puzzles",            label: "Puzzles",              icon: PuzzlePiece },
  { to: "/rooms",                 labelKey: "chip.rooms",              label: "Rooms",                icon: Hash },
  { to: "/search",                labelKey: "chip.search",             label: "Search",               icon: MagnifyingGlass },
  { to: "/sermons",               labelKey: "chip.sermons",            label: "Sermons",              icon: Microphone },
  { to: "/settings",              labelKey: "chip.settings",           label: "Settings",             icon: UserCircle },
  { to: "/study-group",           labelKey: "chip.studyGroup",         label: "Study Group",          icon: Users },
  { to: "/recipes",               labelKey: "chip.sundayDinner",       label: "Sunday Dinner",        icon: ForkKnife, fallbackLabel: true },
  { to: "/sunday-school",         labelKey: "chip.sundaySchool",       label: "Sunday School",        icon: BookBookmark },
  { to: "/support",               labelKey: "chip.supportUs",          label: "Support Us",           icon: Heart },
  { to: "/wall",                  labelKey: "chip.wallOfFaith",        label: "Wall of Faith",        icon: Heart },
  { to: "/lessons",               labelKey: "chip.wedLessons",         label: "Wed Lessons",          icon: BookBookmark },
];
// Sort alphabetically (case-insensitive) by the English source label
// as a stable fallback. The runtime rail re-sorts by the *translated*
// label using i18n inside the component so non-English users still
// see A→Z in their own language.
_ALPHA.sort((a, b) => a.label.localeCompare(b.label, "en", { sensitivity: "base" }));
// Dedupe by route — any chip that's been promoted into _PINNED is
// dropped from _ALPHA so it doesn't render twice in the rail. Keeps
// the alphabetical block's data intact (search/jump tools that scan
// the master list still see Faith-Based Rap under F) but only one
// visible chip per destination.
const _PINNED_TO = new Set(_PINNED.map((c) => c.to));
const CATEGORIES = [..._PINNED, ..._ALPHA.filter((c) => !_PINNED_TO.has(c.to))];

export default function CategoryChipRail() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const railRef = useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(true);
  // The chip rail sits as a second sticky strip directly below the
  // app header. The header's height varies by device (notched
  // iPhones add `env(safe-area-inset-top)` on top of its content), so
  // hardcoding `top-[64px]` left a cream gap between the two bars
  // exactly equal to the notch's height during scroll. Measure the
  // header element's real height once on mount + on resize, then
  // use that value as our sticky offset. ResizeObserver catches the
  // case where the header grows (e.g., when the user logs in and a
  // free-trial banner appears inside it).
  const [headerHeight, setHeaderHeight] = useState(64);
  useEffect(() => {
    const measure = () => {
      const el = document.querySelector('[data-testid="app-header"]');
      if (el) setHeaderHeight(el.getBoundingClientRect().height);
    };
    measure();
    const ro = (typeof window !== "undefined" && window.ResizeObserver)
      ? new ResizeObserver(measure)
      : null;
    const el = document.querySelector('[data-testid="app-header"]');
    if (ro && el) ro.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("resize", measure);
      if (ro) ro.disconnect();
    };
  }, []);
  // Personalization tick removed in Feb 2026 — the rail is now
  // strictly alphabetical (with Home + Trending pinned). We keep
  // `activeRoute` highlighting for the current page but no longer
  // re-rank chips by visit count.

  // Match the active chip on prefix so e.g. `/bible/john/3` still
  // highlights the "Bible" chip. Sorted longest-first so a more
  // specific route (e.g. `/movies/black-american`) beats a parent.
  const activeRoute = React.useMemo(() => {
    const sorted = [...CATEGORIES].sort((a, b) => b.to.length - a.to.length);
    const found = sorted.find((c) => {
      if (c.to === "/") return location.pathname === "/";
      return location.pathname === c.to || location.pathname.startsWith(c.to + "/");
    });
    return found ? found.to : null;
  }, [location.pathname]);

  // Visit-tracking dropped (Feb 2026) — see comment above where
  // `orderedCategories` is defined. We still highlight the active
  // chip and auto-scroll it into view, but we no longer mutate
  // localStorage on every navigation.

  // Alphabetical ordering — Home + Trending pinned at indexes 0+1,
  // every other category renders in A→Z order based on the *active
  // language's* translated label so a Spanish user sees "Apocrifos,
  // Biblia, …" in true Spanish alphabetical order rather than the
  // English source order. Re-runs whenever the user flips the
  // language picker.
  const orderedCategories = React.useMemo(() => {
    const sorted = [..._ALPHA]
      .filter((c) => !_PINNED_TO.has(c.to)) // dedupe — pinned chips don't repeat in the alpha block
      .sort((a, b) => {
        const la = (a.labelKey ? t(a.labelKey) : a.label) || a.label;
        const lb = (b.labelKey ? t(b.labelKey) : b.label) || b.label;
        return la.localeCompare(lb, i18n.language || "en", { sensitivity: "base" });
      });
    return [..._PINNED, ...sorted];
  }, [t, i18n.language]);

  // Auto-scroll the active chip into view on mount / route change so
  // a user who deep-links into /apocrypha can see they're in that
  // section without dragging the rail.
  useEffect(() => {
    const rail = railRef.current;
    if (!rail || !activeRoute) return;
    const chip = rail.querySelector(`[data-chip-to="${CSS.escape(activeRoute)}"]`);
    if (chip && typeof chip.scrollIntoView === "function") {
      chip.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  }, [activeRoute]);

  // Recompute chevron visibility on scroll so each side only shows
  // when there's actually more rail in that direction.
  const updateChevrons = () => {
    const rail = railRef.current;
    if (!rail) return;
    setShowLeft(rail.scrollLeft > 6);
    setShowRight(rail.scrollLeft + rail.clientWidth < rail.scrollWidth - 6);
  };
  useEffect(() => {
    updateChevrons();
    const rail = railRef.current;
    if (!rail) return;
    rail.addEventListener("scroll", updateChevrons, { passive: true });
    window.addEventListener("resize", updateChevrons);
    return () => {
      rail.removeEventListener("scroll", updateChevrons);
      window.removeEventListener("resize", updateChevrons);
    };
  }, []);

  const scrollBy = (dir) => {
    const rail = railRef.current;
    if (!rail) return;
    const delta = Math.round(rail.clientWidth * 0.8) * (dir === "left" ? -1 : 1);
    rail.scrollBy({ left: delta, behavior: "smooth" });
  };

  // Mouse/pointer-drag pan. Touch users already get free horizontal
  // scroll via `overflow-x-auto`, but desktop users on a trackpad or
  // a non-Apple mouse benefit from a click-and-drag affordance.
  //
  // CRITICAL: we do NOT call `setPointerCapture` here. Capturing the
  // pointer on the rail meant every pointerup landed on the rail
  // instead of on the chip the user actually tapped — silently
  // swallowing taps on Home / Trending / Bible / etc. Feb 2026
  // user report: "buttons on the scrollable toolbar don't work".
  // Without capture, child Link clicks fire normally, AND a real
  // drag still works because we move the rail's scrollLeft on each
  // pointermove (no capture needed for that).
  const dragStateRef = useRef({ active: false, startX: 0, startScroll: 0, moved: 0 });
  const onPointerDown = (e) => {
    const rail = railRef.current;
    if (!rail) return;
    dragStateRef.current = {
      active: true,
      startX: e.clientX,
      startScroll: rail.scrollLeft,
      moved: 0,
    };
  };
  const onPointerMove = (e) => {
    const st = dragStateRef.current;
    if (!st.active) return;
    const rail = railRef.current;
    if (!rail) return;
    const dx = e.clientX - st.startX;
    if (Math.abs(dx) > 4) {
      st.moved += Math.abs(dx);
      rail.scrollLeft = st.startScroll - dx;
    }
  };
  const onPointerUp = (e) => {
    const st = dragStateRef.current;
    const wasDragging = st.active && st.moved > 6;
    dragStateRef.current = { active: false, startX: 0, startScroll: 0, moved: 0 };
    if (wasDragging) {
      // Real drag — swallow the click that would have fired so the
      // user doesn't navigate to whatever chip the drag started on.
      e.preventDefault();
      e.stopPropagation();
    }
    // Otherwise: clean tap — let the native click on the chip fire.
  };

  // Toggle the side QuickShortcutsRail. Same localStorage key the rail
  // itself uses, plus a synthetic `storage` event so the rail picks up
  // the change immediately (without it the rail would only refresh on
  // its own mount or on a route change).
  const toggleShortcutRail = () => {
    try {
      const prev = window.localStorage.getItem("hw-shortcut-rail-open") === "1";
      window.localStorage.setItem("hw-shortcut-rail-open", prev ? "0" : "1");
      window.dispatchEvent(new StorageEvent("storage", {
        key: "hw-shortcut-rail-open",
        newValue: prev ? "0" : "1",
      }));
    } catch (_) {}
  };

  return (
    <div
      data-testid="category-chip-rail"
      className="sticky z-40 bg-cream/85 backdrop-blur-md border-b border-cream-300/70"
      style={{
        // Sit flush against the bottom of the sticky <header>. The
        // header's height is measured live in useEffect above so
        // notched phones (where safe-area adds ~44 px to the
        // header) no longer show a cream gap between the two bars
        // while the user scrolls.
        top: `${headerHeight}px`,
      }}
    >
      <div className="relative max-w-7xl mx-auto flex items-stretch">
        {/* HW outline-logo button — pinned at the far left of the rail.
            Tapping it toggles the side QuickShortcutsRail open/closed,
            same localStorage key (`hw-shortcut-rail-open`) so the two
            controls stay in lock-step. Outlined / sketchy style so the
            button doesn't compete with the colorful active chip. */}
        <button
          type="button"
          onClick={toggleShortcutRail}
          data-testid="chip-rail-logo-btn"
          aria-label="Toggle quick shortcuts"
          title="Open shortcuts — quick jump to Read, Rooms, Movies, Gospel, Prayer, Sermons…"
          className="shrink-0 self-center ml-2 md:ml-3 w-10 h-10 grid place-items-center rounded-full border-2 border-forest/40 text-forest hover:border-forest hover:bg-forest/10 transition-all"
        >
          <BrandMarkOutline size={26} color="currentColor" />
        </button>

        <div className="relative flex-1 min-w-0">
        {/* Left fade + chevron */}
        {showLeft && (
          <>
            <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-10 bg-gradient-to-r from-cream to-transparent z-10 rail-fade-left" />
            <button
              type="button"
              onClick={() => scrollBy("left")}
              data-testid="chip-rail-prev"
              aria-label="Scroll categories left"
              className="hidden md:grid absolute left-1 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-cream-200 hover:bg-cream-300 text-ink place-items-center shadow-md z-20"
            >
              <CaretLeft size={12} weight="bold" />
            </button>
          </>
        )}

        <div
          ref={railRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          style={{ touchAction: "pan-x", overscrollBehaviorX: "contain" }}
          className="flex items-center gap-2 overflow-x-auto scroll-smooth py-2.5 px-3 md:px-12 cursor-grab active:cursor-grabbing select-none [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {orderedCategories.map((cat, catIdx) => {
            const Icon = cat.icon;
            const active = cat.to === activeRoute;
            // Show the "NEW" badge until its expiry date passes.
            // Date.parse is a safe comparison — past-expiry chips
            // silently drop the badge with no code change required.
            const showBadge = cat.badge && (() => {
              const exp = cat.badgeUntil ? Date.parse(cat.badgeUntil) : NaN;
              return Number.isFinite(exp) ? Date.now() <= exp : true;
            })();
            return (
              <React.Fragment key={cat.to}>
              <Link
                to={cat.to}
                data-chip-to={cat.to}
                data-testid={`chip-${cat.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 h-9 rounded-full text-[12px] font-semibold whitespace-nowrap transition-all ${
                  active
                    ? "bg-forest text-cream shadow-md"
                    : "bg-cream-200 text-ink hover:bg-cream-300"
                }`}
              >
                <Icon size={13} weight={active ? "fill" : "bold"} />
                {cat.labelKey ? t(cat.labelKey) : cat.label}
                {showBadge && (
                  <span
                    data-testid={`chip-badge-${cat.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[8px] tracking-[0.15em] font-bold leading-none ${
                      active ? "bg-cream text-forest" : "bg-terracotta text-cream"
                    }`}
                  >
                    {cat.badge}
                  </span>
                )}
              </Link>
              {/* "My page" — your avatar, right next to Home. One tap
                  opens your own channel page (owner request June 2026). */}
              {catIdx === 0 && user && (
                <Link
                  to="/foryou?channel=me"
                  data-testid="nav-my-page"
                  title="My page"
                  className="shrink-0 inline-flex items-center gap-1.5 pl-1 pr-3.5 h-9 rounded-full text-[12px] font-semibold whitespace-nowrap bg-cream-200 text-ink hover:bg-cream-300 transition-all"
                >
                  <UserAvatar user={{ id: user.id, name: user.name, avatar_url: user.avatar_url }} size={28} />
                  My page
                </Link>
              )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Right fade + chevron */}
        {showRight && (
          <>
            <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-10 bg-gradient-to-l from-cream to-transparent z-10 rail-fade-right" />
            <button
              type="button"
              onClick={() => scrollBy("right")}
              data-testid="chip-rail-next"
              aria-label="Scroll categories right"
              className="hidden md:grid absolute right-1 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-cream-200 hover:bg-cream-300 text-ink place-items-center shadow-md z-20"
            >
              <CaretRight size={12} weight="bold" />
            </button>
          </>
        )}
        </div>
      </div>
    </div>
  );
}
